<template>
    <div class="wrap-slides-container">
        <div v-if="isItemsVisible" :id="lineId" class="slides-container">
            <div 
                id="slides"
                class="slides-container__slides"
                :class="{'isFocused': isFocused}"
                tabindex="0"
                @focusin="onFocusIn"
                @focusout="onFocusOut"
            >
                <MostPopularSlide
                    v-for="(slide, index) in props.slides"
                    :id="getId(index)"
                    :key="calculateId(slide)"
                    :ref="getId(index)"
                    :card-data="slide"
                    :index="index"
                    :is-active="index==activeIndex"
                    :is-parentally-restricted="checkParentalAccess(slide)"
                />
                <MostPopularInfo
                    :active-slide="activeSlide"
                    :channel-logo="channelLogo"
                    :title="slideInfoTitle"
                    :channel-name="channelName"
                    @on-item-selected="onItemSelected"
                />
            </div>
            <div v-if="isDotsVisible" class="slides-container__dots">
                <div 
                    v-for="(_dot, index) in dots" :key="`dots_ + ${index}`"
                    class="slides-container__dot"
                    :class="{'slides-container__dot_active': index==activeIndex}"
                    @mouseover="onMouseOverDots"
                    @mouseout="onMouseOutDots"
                    @click="setActiveSlide(index)"
                />
            </div>
        </div>
        <div v-else class="slides-skeleton"/>
    </div>
</template>

<script setup lang="ts">
    import type { PropType, Ref} from "vue";
    import { getCurrentInstance, nextTick, computed, watch, onMounted, onBeforeUnmount, ref } from "vue";
    import type { ExposeInterface, MostPopularSlideData } from "./types";
    import { useApplicationStore } from "@/stores/Application";
    import type { Advisory } from "@ca.vmedia.tv/swagger-api";
    import type { Genre, MediaImage } from "@ca.vmedia.tv/domains";
    import MostPopularSlide from "./components/MostPopularSlide.vue";
    import MostPopularInfo from "./components/MostPopularInfo.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useGenresStore } from "@/stores/Genres";
    import { ImgUrlUtils } from '@/utils';
    import { useMostPopularVod } from "@/stores/MostPopularVod";
    import { storeToRefs } from 'pinia';
    import { useParentControlStore } from "@/stores/ParentControl";

    const ApplicationStore = useApplicationStore();
    const { addSelectedIdProgram, hideHeader } = ApplicationStore;

    const { getGenre } = useGenresStore();

    const ParentControlStoreModule = useParentControlStore();
    const {
        SetIncorrectAdultPin,
        ShowParentModal,
        SetTargetWidgetAdultControl
    } = ParentControlStoreModule;
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl
    } = storeToRefs(ParentControlStoreModule);

    const MostPopularVodPageStore = useMostPopularVod();
    const {
        setIndexMostPopular,
    } = MostPopularVodPageStore;
    const {
        getIndexMostPopular: indexMostPopular
    } = storeToRefs(MostPopularVodPageStore);

    const props = defineProps({
        /**
         * Unique key identifier for data query purposes
         */
        keyData: {
            type: String,
            required: true
        },
        /**
         * The items to display on this line. Should be derived from SlideData class
         */
        slides: {
            type: [Array, null] as PropType<MostPopularSlideData[] | null>,
            required: true,
            default: null,
        },
        /**
         * The line unique identifier for the navigation purposes
         */
        lineId: {
            type: String,
            required: true
        },
        /**
         * The name of skeleton
         */
        skeleton: {
            type: String,
            required: true
        },
        /**
         * Maximum number of elements in a line
         */
        limitItems: {
            type: Number,
            required: true
        },
        /**
         * The Line configuration
         */
        config: {
            type: Object,
            required: true
        },
    });

    const internalInstance = getCurrentInstance();
    const { $navigator, $domains } = useNuxtApp();
    const emit = defineEmits(['on-item-selected', 'ready', 'slide-up', 'slide-down']);
    const { setCurrentTimes, needsUnlockPin } = useParentControl();
    const activeIndex: Ref<number> = ref(0);
    const isFocused: Ref<boolean> = ref(false);
    const timerAutoplay: Ref<number | null> = ref(null);
    const channelLogos: Ref<Record<number, string | undefined> | null> = ref(null);

    const dots = computed((): MostPopularSlideData[] => props.slides != null ? props.slides : []);
    const isDotsVisible = computed((): boolean => props.slides !== null && props.slides.length > 1);
    const isItemsVisible = computed((): boolean => props.slides !== null && props.slides.length > 0);
    const lastIndex = computed((): number => {
        if (props.slides != null && props.slides.length > 0) {
            return props.slides.length - 1;
        }
        return -1;
    });
    const nextIndex = computed((): number => activeIndex.value == lastIndex.value ? 0 : activeIndex.value + 1);
    const prevIndex = computed((): number => activeIndex.value == 0 ? lastIndex.value : activeIndex.value - 1);
    const activeSlide = computed((): MostPopularSlideData | null => props.slides ? props.slides[activeIndex.value] : null);
    const channelLogo = computed((): string | null | undefined => {
        if (channelLogos.value && activeSlide.value) {
            return channelLogos.value[Number(activeSlide.value.getId())];
        }
        return null;
    });

    const slideInfoTitle = computed((): string => activeSlide.value?.getName() ?? '');
    const topGenre = computed((): Genre | null => {
        const id: number | null = activeSlide.value?.getPlayItem().topGenreId ?? null;
        if (id) {
            return getGenre(id);
        }
        return null;
    });
    const channelName = computed((): string => topGenre.value && topGenre.value.name ? topGenre.value.name : '');

    const fetchLogo = async (images: MediaImage[] | null, defaultImg: string | null): Promise<string> => {
        const logoImage = await $domains.imagesRepository.getWhiteVodGenreImage(images);
        const channelLogoWidth = 96;

        if (!logoImage) {
            return ImgUrlUtils.src(defaultImg, channelLogoWidth);
        }
        return ImgUrlUtils.src(logoImage, channelLogoWidth);
    };
    const getChannelLogos = async (): Promise<void> => {
        const mapedSlides: Array<[string, string] | null> = await Promise.all(
            props.slides?.map(async (item): Promise<[string, string] | null> => {
                const id: number | null = item.getPlayItem().topGenreId;
                if (id != null) {
                    const genre = getGenre(id);
                    if (genre) {
                        return [item.getId(), await fetchLogo(genre.images, genre.image)];
                    }
                }
                return null;
            }) ?? []
        );
        const filteredSlides = mapedSlides.filter((slide): slide is [string, string] => slide !== null);

        if (filteredSlides.length === 0) {
            return;
        }

        channelLogos.value = Object.fromEntries(filteredSlides);
    };

    const checkParentalAccess = (slide: MostPopularSlideData): boolean => {
        const advisory = slide.getPlayItem()?.advisory;
        return typeof advisory === 'number' ? needsUnlockPin(advisory) : false;
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:enter", onItemSelected);
        $navigator.on("keypress:up", slideUp);
        $navigator.on("keypress:down", slideDown);
        $navigator.on("keypress:left", slidePrev);
        $navigator.on("keypress:right", slideNext);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:enter", onItemSelected);
        $navigator.off("keypress:up", slideUp);
        $navigator.off("keypress:down", slideDown);
        $navigator.off("keypress:left", slidePrev);
        $navigator.off("keypress:right", slideNext);
    };
    const focusSlider = (): void => {
        document.getElementById('slides')?.focus();
    };
    const onMouseOverDots = (): void => {
        stop();
    };
    const onMouseOutDots = (): void => {
        start();
    };
    const goToTop = (): void => {
        window.scrollTo(0, 0);
    };
    const onFocusIn = (): void => {
        hideHeader();

        stop();
        isFocused.value = true;
        registerKeyListeners();
        goToTop();
    };
    const onFocusOut = (): void => {
        if (!isTypeModeEnabledPin.value) {
            start();
        }
        isFocused.value = false;
        unregisterKeyListeners();
    };
    const autoplay = (time: number): void => {
        timerAutoplay.value = window.setInterval(
            () => {
                slideNext();
            },
            time
        );
    };
    const clearAllTimer = () => {
        if (timerAutoplay.value) {
            clearInterval(timerAutoplay.value);
        }
    };
    const stop = (): void => {
        clearAllTimer();
    };
    const start = ()=> {
        const config = props.config;
        if (config.isAutoplay) {
            autoplay(config.speedAutoplay);
        }
    };
    const slideUp = (): void => {
        emit('slide-up');
    };
    const slideDown = (): void => {
        emit('slide-down');
    };
    const slideNext = (): void => {
        activeIndex.value = nextIndex.value;
    };
    const slidePrev = (): void => {
        activeIndex.value = prevIndex.value;
    };
    const setActiveSlide = (index: number): void => {
        activeIndex.value = index;
    };
    const calculateId = (item: MostPopularSlideData): string => {
        return item.getId();
    };
    const getId = (index: number): string => {
        if (props.slides?.[index] && props.slides[index].getId()) {
            return `${props.lineId}_${(props.slides[index]).getId()}`;
        }
        return "";
    };
    const onItemSelected = (): void => {
        if (props.slides != null) {
            const item: MostPopularSlideData = props.slides[activeIndex.value];

            addSelectedIdProgram('slides')

            setIndexMostPopular(
                activeIndex.value
            );
            const isAllow = checkParentControl(item);
            if (isAllow) {
                onListItemSelected(item, props.lineId);
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(String(internalInstance?.uid))
            }
        }
    };
    const checkParentControl = (item: MostPopularSlideData): boolean => {
        const advisory: Advisory | null | undefined = item.getPlayItem()?.advisory;
        return typeof advisory === 'number' ? !needsUnlockPin(advisory) : true;
    };
    const onListItemSelected = (selectedItem: MostPopularSlideData, lineId: string): void => {
        if (selectedItem && lineId) {
            emit("on-item-selected", {item: selectedItem, lineId: lineId});
        }
    };
    const init = (): void => {
        setActiveSlide(indexMostPopular.value);
    };

    init();

    watch(
        isAdultPinCorrect,
        (value: boolean) => {
            if (value && internalInstance?.uid === Number(targetWidgetAdultControl.value)) {
                SetIncorrectAdultPin();

                setCurrentTimes();

                if (props.slides) {
                    onListItemSelected(props.slides[activeIndex.value], props.lineId);
                }
            }
        }
    );
    watch(
        isTypeModeEnabledPin,
        (value: boolean) => {
            if (value) {
                stop();
            }
        }
    );
    watch(
        () => props.slides,
        (value) => {
            if (value != null) {
                void nextTick(() => {
                    emit('ready');
                });
            }
        }
    );
    watch(() => props.slides, (value, oldValue) => {
        if (value && value.length > 0 && JSON.stringify(value) !== JSON.stringify(oldValue)) {
            getChannelLogos().catch((error) => {
                Logger.captureError(error as Error);
            });
        }
    });

    onMounted(() => {
        start();
        if (props.slides !== null) {
            emit('ready');
            if (!channelLogos.value) {
                getChannelLogos().catch((error) => {
                    Logger.captureError(error as Error);
                });
            }
        }
    });

    onBeforeUnmount(() => {
        clearAllTimer();
    });

    defineExpose<ExposeInterface>({
        focusSlider,
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
